'use client'

import MapIcon from '@/assets/icons/map.svg'
import { Button } from '@/core/ui/atoms/button'
import { BaseCombobox } from '@/core/ui/forms/combobox'
import { AirportModel } from '@/features/locations/types/models/airport.model'
import { Place } from '@/features/locations/ui'
import { Check } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { Controller, useFormContext } from 'react-hook-form'

type Props = {
  field: 'from' | 'to'
  airports: AirportModel[]
  loading: boolean
  searching: boolean
  onSelectMap: () => void
  onSearch?: (value: string) => void
  onLocationChange: (field: 'from' | 'to', location: AirportModel) => void
  onClear?: () => void
  resetSearch?: () => void
}

export const LocationInput: React.FC<Props> = ({
  field,
  airports,
  loading,
  searching,
  onSelectMap,
  onLocationChange,
  onSearch,
  onClear,
  resetSearch,
}) => {
  const t = useTranslations()
  const {
    formState: { errors },
    control,
  } = useFormContext()

  const handleSelect = (placeId: string | number | null) => {
    if (!placeId) {
      return
    }
    const airport = airports.find(
      a => a.place_id.toString() === placeId.toString(),
    )
    if (!airport) return
    onLocationChange(field, airport)
  }

  return (
    <Controller
      name={`${field}Point`}
      control={control}
      render={({ field: { onChange, value } }) => (
        <BaseCombobox
          isLoading={loading || searching}
          popoverClassName='bg-white'
          disableDefaultStyles
          resetSearch={resetSearch}
          disabled={loading}
          placeholder={
            <span className='text-gray-500'>{t(`forms.${field}PH`)}</span>
          }
          items={
            airports.map(a => ({ ...a, label: a.name, value: a.place_id })) ||
            []
          }
          error={Boolean(errors[`${field}PointName`])}
          errorMessage={errors[`${field}PointName`]?.message?.toString()}
          onChange={v => {
            handleSelect(v)
            onChange(v)
          }}
          value={value}
          onClear={onClear}
          startAction={({ onClick }) => (
            <Button
              startContent={<MapIcon className='fill-red' />}
              variant='text'
              color='red'
              fullWidth
              radius='none'
              type='button'
              justifyContent='start'
              className='text-base hover:bg-gray-100'
              onClick={e => {
                e.stopPropagation()
                onClick()
                onSelectMap()
              }}
            >
              Pick on map
            </Button>
          )}
          renderItem={(airport, selected) => (
            <div className='flex w-full items-center justify-between gap-2'>
              <Place {...airport} />
              {selected ? <Check className='text-xs' /> : null}
            </div>
          )}
          onSearch={value => {
            if (!value) {
              resetSearch?.()
            }
            if (
              !value ||
              airports.some(a => a.name.toLowerCase() === value.toLowerCase())
            ) {
              return
            }
            onSearch?.(value)
          }}
        />
      )}
    />
  )
}
